import React, { useState, useRef, useEffect, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";
import { IconButton } from "@mui/material";
import { Icon } from "js/Components/Icon";
import { trackActivity } from "js/core/utilities/utilities";
import { VIDEO_TASK_CONFIGS } from "./taskConfig";

// Video dimensions (source is 1920×1080, 16:9)
const VIDEO_PREVIEW_WIDTH = 500;
const VIDEO_PREVIEW_HEIGHT = Math.round(500 * (9 / 16)); // 281
const VIDEO_FULL_WIDTH = 854;
const VIDEO_FULL_HEIGHT = Math.round(854 * (9 / 16)); // 480

const FadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const FadeInPreview = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 0.8;
    }
`;

const VideoContainer = styled.div<{ $isEnlarged: boolean; $isPreview: boolean; $skipAnimation: boolean }>`
    position: fixed;
    top: 24px;
    right: 24px;
    width: ${props => props.$isEnlarged ? `${VIDEO_FULL_WIDTH}px` : `${VIDEO_PREVIEW_WIDTH}px`};
    height: ${props => props.$isEnlarged ? `${VIDEO_FULL_HEIGHT}px` : `${VIDEO_PREVIEW_HEIGHT}px`};
    z-index: ${props => props.$isPreview ? "9" : "10"};
    transition: width 0.3s ease, height 0.3s ease;
    cursor: ${props => props.$isPreview ? "default" : "pointer"};
    pointer-events: ${props => props.$isPreview ? "none" : "auto"};
    
    /* fade */
    opacity: ${props => props.$skipAnimation ? (props.$isPreview ? "0.8" : "1") : "0"};
    ${props => {
        if (props.$skipAnimation) return css`animation: none;`;
        if (props.$isPreview) return css`animation: ${FadeInPreview} 800ms ease-out forwards;`;

        // This should run whenever the video is shown for the user for the first time
        return css`animation: ${FadeIn} 600ms ease-out 800ms forwards;`;
    }}
    
    &:hover {
        width: ${props => props.$isPreview ? `${VIDEO_PREVIEW_WIDTH}px` : `${VIDEO_FULL_WIDTH}px`};
        height: ${props => props.$isPreview ? `${VIDEO_PREVIEW_HEIGHT}px` : `${VIDEO_FULL_HEIGHT}px`};
        aspect-ratio: ${VIDEO_FULL_WIDTH}/${VIDEO_FULL_HEIGHT};
    }
`;

const VideoWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 9px 37px 0 rgba(0, 0, 0, 0.20);
    background: #000;
    pointer-events: auto;
`;

const VideoElement = styled.video<{ $isPreview: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: ${props => props.$isPreview ? "brightness(0.7)" : "none"};
`;

const PlayPauseOverlay = styled.div<{ $visible: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.9);
    transition: opacity 0.3s ease, visibility 0.3s ease;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? "visible" : "hidden"};
    pointer-events: ${props => props.$visible ? "auto" : "none"};
    
    &:hover {
        background-color: rgba(255, 255, 255, 1);
    }
`;

const CloseButton = styled(IconButton)`
    &&& {
        position: absolute;
        top: 8px;
        right: 8px;
        color: #FFF;
        width: 32px;
        height: 32px;
        z-index: 10;
        background: none;
    }
`;

const TitleSubtitleOverlay = styled.div<{ $visible: boolean }>`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    z-index: 6;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%);
    transition: opacity 0.3s ease, visibility 0.3s ease;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? "visible" : "hidden"};
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Title = styled.div`
    color: #FFF;
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
`;

const Subtitle = styled.div`
    color: #FFF;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: -0.32px;
`;

interface CoachVideoProps {
    videoUrl: string;
    taskKey: string;
    onClose?: () => void;
    onTaskComplete?: () => void;
    isPreview?: boolean;
}

const MARK_TASK_COMPLETE_TIME_IN_MS = 3000;
const PREVIEW_TIME_SECONDS = 5;

// Our video starts with black screen, so we need to show the first frame to avoid a blank screen as a preview
const showPreviewFrame = (video: HTMLVideoElement, isPreview: boolean) => {
    const t = (Number(video.duration) > 0) ? Math.min(PREVIEW_TIME_SECONDS, video.duration) : 0;
    video.currentTime = t;
    video.muted = true; // Required for autoplay in most browsers
    video.play().then(() => {
        video.pause();
        video.currentTime = t;
    }).finally(() => {
        video.muted = isPreview;
    });
};

const CoachVideo: React.FC<CoachVideoProps> = ({ videoUrl, taskKey, onClose, onTaskComplete, isPreview = false }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isEnlarged, setIsEnlarged] = useState(false);
    const taskCompleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [skipAnimation, setSkipAnimation] = useState(false);

    // reset "started playing" when in preview; skip animation when leaving preview
    const wasPreviewRef = useRef(isPreview);
    const hasStartedPlayingFullRef = useRef(false);
    useEffect(() => {
        if (isPreview) {
            hasStartedPlayingFullRef.current = false;
        }
        if (wasPreviewRef.current && !isPreview) {
            setSkipAnimation(true);
        }
        wasPreviewRef.current = isPreview;
    }, [isPreview]);

    const { title, subtitle } = useMemo(() => {
        const config = taskKey ? VIDEO_TASK_CONFIGS[taskKey] : null;
        const { title = "", subtitle = "" } = config || {};
        return { title, subtitle };
    }, [taskKey]);

    // Full mode only: play/pause/ended handlers (task complete, analytics)
    useEffect(() => {
        const video = videoRef.current;
        if (!video || isPreview) return;

        const onPlay = () => {
            setIsPlaying(true);
            setIsHovering(false);
            trackActivity("OnboardingVideo", "Started");
            taskCompleteTimeoutRef.current = setTimeout(() => onTaskComplete?.(), MARK_TASK_COMPLETE_TIME_IN_MS);
        };
        const onPause = () => {
            setIsPlaying(false);
            if (taskCompleteTimeoutRef.current) {
                clearTimeout(taskCompleteTimeoutRef.current);
                taskCompleteTimeoutRef.current = null;
            }
        };
        const onEnded = () => {
            setIsPlaying(false);
            if (taskCompleteTimeoutRef.current) clearTimeout(taskCompleteTimeoutRef.current);
            handleClose(true);
        };

        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("ended", onEnded);
        return () => {
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("ended", onEnded);
            if (taskCompleteTimeoutRef.current) clearTimeout(taskCompleteTimeoutRef.current);
        };
    }, [isPreview, onTaskComplete, taskKey]);

    // When video loaded, show frame at PREVIEW_TIME_SECONDS (avoids black first frame in both preview and full mode)
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const onLoaded = () => showPreviewFrame(video, isPreview);
        if (video.readyState >= 2) onLoaded();
        else video.addEventListener("loadeddata", onLoaded, { once: true });
        return () => video.removeEventListener("loadeddata", onLoaded);
    }, [videoUrl, isPreview]);

    const handlePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        setIsEnlarged(true);
        if (isPlaying) {
            video.pause();
            return;
        }

        const startFromZero = video.ended || (!isPreview && !hasStartedPlayingFullRef.current);
        if (startFromZero) {
            video.currentTime = 0;
            if (!isPreview) hasStartedPlayingFullRef.current = true;
        }
        video.play();
    };

    const handleVideoClick = (e: React.MouseEvent) => {
        // Don't toggle if clicking on overlay or close button
        const target = e.target as HTMLElement;
        if (target.closest("[data-overlay]") || target.closest("[data-close-button]")) {
            return;
        }
        setIsEnlarged(true);
        handlePlayPause();
    };

    const handleClose = (completed = false) => {
        const video = videoRef.current;

        if (video) {
            video.pause();
            trackActivity("OnboardingVideo", "Ended", null, null, {
                timestamp: Math.round(video.currentTime),
                // Represents if the video was completed or not
                // if it was closed manually, it was not completed
                completed
            });
        }
        if (taskCompleteTimeoutRef.current) {
            clearTimeout(taskCompleteTimeoutRef.current);
        }

        onClose?.();
    };

    const showPlayPauseOverlay = !isPlaying || isHovering;
    const showTitleSubtitle = isPreview || !isPlaying || (isPlaying && isHovering);

    return (
        <VideoContainer
            $isEnlarged={isEnlarged}
            $isPreview={isPreview}
            $skipAnimation={skipAnimation}
            onMouseEnter={() => !isPreview && setIsHovering(true)}
            onMouseLeave={() => !isPreview && setIsHovering(false)}
        >
            <VideoWrapper onClick={isPreview ? undefined : handleVideoClick}>
                <VideoElement
                    ref={videoRef}
                    playsInline
                    preload="auto"
                    muted={isPreview}
                    $isPreview={isPreview}
                >
                    <source src={videoUrl} type="video/mp4" />
                </VideoElement>

                <TitleSubtitleOverlay $visible={showTitleSubtitle}>
                    <Title>{title}</Title>
                    <Subtitle>{subtitle}</Subtitle>
                </TitleSubtitleOverlay>

                <PlayPauseOverlay
                    data-overlay
                    $visible={showPlayPauseOverlay}
                    onClick={handlePlayPause}
                >
                    <Icon fill large color="#000">
                        {isPlaying ? "pause" : "play_arrow"}
                    </Icon>
                </PlayPauseOverlay>
                <CloseButton
                    data-close-button
                    onClick={() => handleClose(true)}
                >
                    <Icon color="black" medium style={{ background: "white", borderRadius: "50px" }}>close</Icon>
                </CloseButton>

            </VideoWrapper>
        </VideoContainer>
    );
};

export default CoachVideo;

